import React, { useEffect, useState, useContext } from "react";
import axios from "../api/axios";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { AuthContext } from "../context/AuthContext";
import CommonTable from "../components/CommonTable";
import { useMessage } from "../context/MessageContext";
import { useConfirm } from "../context/ConfirmContext";

const OrgFormDialog = ({ open, onClose, onSave, org }) => {
  const [form, setForm] = useState(org || { name: "", shortCode: "" });
  useEffect(() => {
    setForm(org || { name: "", shortCode: "" });
  }, [org]);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = () => onSave(form);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{org ? "Edit" : "Add"} Organization</DialogTitle>
      <DialogContent>
        <TextField
          label="Name"
          name="name"
          value={form.name}
          onChange={handleChange}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          label="Short Code"
          name="shortCode"
          value={form.shortCode}
          onChange={handleChange}
          fullWidth
          margin="normal"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {org ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Organizations = () => {
  const { user } = useContext(AuthContext);
  const { showMessage } = useMessage();
  const { confirm } = useConfirm();

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);

  const fetchOrgs = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/orgs");
      setOrgs(res.data);
    } catch (err) {
      console.error(err);
      showMessage("Failed to load organizations", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgs();
  }, []);

  const handleAdd = () => {
    setEditingOrg(null);
    setOpen(true);
  };

  const handleEdit = (org) => {
    setEditingOrg(org);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      title: "Confirm Delete",
      message: "Are you sure you want to delete this organization?",
    });
    if (confirmed) {
      try {
        await axios.delete(`/orgs/${id}`);
        showMessage("Organization deleted", "success");
        fetchOrgs();
      } catch (err) {
        console.error(err);
        showMessage("Failed to delete organization", "error");
      }
    }
  };

  const handleSave = async (form) => {
    try {
      if (editingOrg) {
        await axios.put(`/orgs/${editingOrg._id}`, form);
        showMessage("Organization updated", "success");
      } else {
        await axios.post("/orgs", form);
        showMessage("Organization added", "success");
      }
      setOpen(false);
      fetchOrgs();
    } catch (err) {
      console.error(err);
      showMessage("Failed to save organization", "error");
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Organizations
      </Typography>
      {user.role === "super_admin" && (
        <Button variant="contained" onClick={handleAdd} sx={{ mb: 2 }}>
          Add Organization
        </Button>
      )}
      <CommonTable
        columns={[
          { label: "Name", field: "name" },
          { label: "Short Code", field: "shortCode" },
          {
            label: "Actions",
            sortable: false,
            renderCell: (org) => (
              <>
                <Button
                  size="small"
                  onClick={() => handleEdit(org)}
                  startIcon={<Edit />}
                >
                  Edit
                </Button>
                {user.role === "super_admin" && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleDelete(org._id)}
                    startIcon={<Delete />}
                  >
                    Delete
                  </Button>
                )}
              </>
            ),
          },
        ]}
        rows={orgs}
        rowsPerPageOptions={[5, 10, 25]}
      />
      <OrgFormDialog
        open={open}
        onClose={() => setOpen(false)}
        onSave={handleSave}
        org={editingOrg}
      />
    </Box>
  );
};
export default Organizations;
